/**
 * Reset Password Modal (password links)
 *
 * The admin's way to get a user into their account. Admins never type, set or
 * see another user's password. Two modes:
 *  - Generate a link (default): a one-time link the admin copies and shares
 *    out of band — for users who cannot receive email, or need it now. It is
 *    shown exactly once; the server keeps only its hash. A PENDING user gets a
 *    first-password link, everyone else a reset link (the server decides).
 *  - Email a reset link: the public forgot-password flow, so the link goes
 *    straight to the user's inbox.
 *
 * Below both, the user's recent password links: who generated each, when,
 * what for, and what became of it.
 *
 * Self-contained (owns its hooks and state) so any surface holding a `User`
 * can open it: Admin → Users row menu, the per-user admin pages.
 */

import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  useToast,
} from '@lognai/synapse-design-system-v2';
import { ApiError, formatDate, getCurrentUserId } from '@lognai/plugin-sdk';
import type { User } from '@lognai/core-platform';
import type { PasswordLink, PasswordLinkView } from '@lognai/plugin-admin';
import { FormModal } from '../shared';
import {
  useGeneratePasswordLink,
  usePasswordLinks,
  useSendPasswordResetLink,
  useUser,
} from '../../hooks';
import { PasswordLinkResult, useLinkCloseGuard } from './PasswordLinkResult';

type ResetMode = 'generate' | 'email';

export interface ResetPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
}

const PURPOSE_LABELS: Record<PasswordLinkView['purpose'], string> = {
  ADMIN_RESET: 'Reset',
  SET_INITIAL: 'First password',
};

const OUTCOME_BADGES: Record<
  PasswordLinkView['outcome'],
  { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }
> = {
  OPEN: { label: 'Open', variant: 'default' },
  USED: { label: 'Used', variant: 'secondary' },
  SUPERSEDED: { label: 'Superseded', variant: 'outline' },
  EXPIRED: { label: 'Expired', variant: 'outline' },
};

const formatWhen = (date: Date | string | undefined) => formatDate(date ?? null, 'd MMM yyyy, HH:mm');

/** The generating admin by name; a user the caller cannot read stays anonymous. */
function GeneratedBy({ userId }: { userId: string }) {
  const isSelf = userId === getCurrentUserId();
  const { data } = useUser(isSelf ? undefined : userId, { retry: false, staleTime: 5 * 60_000 });
  if (isSelf) return <>you</>;
  return <>{data?.fullName || data?.email || 'another administrator'}</>;
}

function PasswordLinkHistory({ userId, enabled }: { userId: string; enabled: boolean }) {
  const { data: links, isLoading } = usePasswordLinks(userId, { enabled });

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">Recent password links</h3>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !links || links.length === 0 ? (
        <p className="text-sm text-muted-foreground">No password links have been generated.</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {links.map((link) => {
            const outcome = OUTCOME_BADGES[link.outcome];
            return (
              <li
                key={link.id}
                data-testid="password-link-row"
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <span className="font-medium">{PURPOSE_LABELS[link.purpose]}</span>
                  <span className="block text-xs text-muted-foreground">
                    {formatWhen(link.createdAt)} by <GeneratedBy userId={link.generatedBy} />
                    {link.usedAt ? ` · used ${formatWhen(link.usedAt)}` : ''}
                  </span>
                </div>
                <Badge variant={outcome.variant}>{outcome.label}</Badge>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function ResetPasswordModal({ open, onOpenChange, user }: ResetPasswordModalProps) {
  const { toast } = useToast();
  const sendLink = useSendPasswordResetLink();
  const generateLink = useGeneratePasswordLink();

  const [mode, setMode] = useState<ResetMode>('generate');
  const [generated, setGenerated] = useState<PasswordLink | null>(null);
  const [error, setError] = useState<string | null>(null);

  const guard = useLinkCloseGuard(generated !== null, onOpenChange);
  const { reset: resetGenerateLink } = generateLink;
  const { reset: resetGuard } = guard;

  // A generated link is shown once: never carry it over to the next opening,
  // in this component or in the mutation cache.
  useEffect(() => {
    if (!open) {
      setMode('generate');
      setGenerated(null);
      setError(null);
      resetGenerateLink();
      resetGuard();
    }
  }, [open, resetGenerateLink, resetGuard]);

  const userLabel = user.fullName || user.email;
  const isSubmitting = sendLink.isPending || generateLink.isPending;

  const handleSubmit = async () => {
    setError(null);
    try {
      if (mode === 'generate') {
        // Cooldown, self-target and inactive-target refusals come back as a
        // 400 whose message says what to do (incl. when the cooldown lifts).
        setGenerated(await generateLink.mutateAsync(user.id));
        return;
      }
      await sendLink.mutateAsync(user.email);
      toast({
        title: 'Reset link sent',
        description: `${user.email} will receive a link to choose a new password. It expires in 1 hour.`,
      });
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof ApiError && err.details?.status === 429
          ? 'Too many reset requests. Wait a few minutes and try again.'
          : err instanceof Error
            ? err.message
            : 'Failed to create a password link',
      );
    }
  };

  return (
    <FormModal
      open={open}
      onOpenChange={guard.requestOpenChange}
      title="Password Link"
      description={`Help ${user.email} set a password. You never see or choose it.`}
      size="md"
      submitLabel={mode === 'generate' ? 'Generate Link' : 'Send Reset Link'}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      hideFooter={generated !== null}
    >
      <div className="space-y-6">
        {generated ? (
          <div className="space-y-4">
            <PasswordLinkResult link={generated} userLabel={userLabel} onCopied={guard.markCopied} />
            <div className="flex justify-end">
              <Button type="button" onClick={() => guard.requestOpenChange(false)}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <RadioGroup value={mode} onValueChange={(value: string) => setMode(value as ResetMode)}>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="generate" id="reset-mode-generate" className="mt-0.5" />
                <Label htmlFor="reset-mode-generate" className="space-y-1 font-normal">
                  <span className="block font-medium">Generate a link to share</span>
                  <span className="block text-sm text-muted-foreground">
                    A one-time link you copy and send them yourself. Shown once; it replaces any
                    earlier link generated for them.
                  </span>
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="email" id="reset-mode-email" className="mt-0.5" />
                <Label htmlFor="reset-mode-email" className="space-y-1 font-normal">
                  <span className="block font-medium">Email a reset link</span>
                  <span className="block text-sm text-muted-foreground">
                    Sent to {user.email}. Expires in 1 hour.
                  </span>
                </Label>
              </div>
            </RadioGroup>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </div>
        )}

        <PasswordLinkHistory userId={user.id} enabled={open} />
      </div>
      {guard.confirmDialog}
    </FormModal>
  );
}
